/**
 * Geometry for the diverging stacked bar chart, computed apart from React so the widget
 * only maps rectangles to SVG and the layout can be tested.
 *
 * The chart is drawn on white with explicit fills because it is exported as SVG and PNG,
 * which carry no stylesheet. Segments are separated by a 2px white gap; a percentage is
 * printed inside a segment only when it is wide enough to hold one.
 */

import {
  type DivergingSegment,
  divergingExtent,
  inkOn,
} from "@/lib/tool-kit/research-design/likert";

export const CHART_WIDTH = 720;
const LABEL_COLUMN = 156;
const RIGHT_MARGIN = 20;
const SIDE_MARGIN = 12;
const BAR_HEIGHT = 22;
const ROW_PITCH = 32;
const LEGEND_ROW = 22;
const LEGEND_SWATCH = 12;
const AXIS_HEIGHT = 30;
/** Rough advance width of a 12px sans-serif character, for wrapping and truncation. */
const CHAR_WIDTH = 6.2;
const MIN_LABELLED_SEGMENT = 30;

export interface ChartSegment {
  x: number;
  width: number;
  fill: string;
  ink: string;
  share: number;
  count: number;
  code: number;
  /** Wide enough to print its percentage inside. */
  labelled: boolean;
}

export interface ChartRow {
  /** The item name, shortened to fit the label column. */
  label: string;
  /** The full item name, for the tooltip. */
  name: string;
  y: number;
  segments: ChartSegment[];
}

export interface ChartLegendEntry {
  x: number;
  y: number;
  fill: string;
  label: string;
}

export interface LikertChartLayout {
  width: number;
  height: number;
  /** Right edge of the item names. */
  labelX: number;
  barHeight: number;
  plotTop: number;
  plotBottom: number;
  centre: number;
  rows: ChartRow[];
  legend: ChartLegendEntry[];
  /** Tick positions and their shares (negative = left of centre). */
  ticks: { x: number; value: number }[];
}

function shorten(text: string, room: number): string {
  const max = Math.floor(room / CHAR_WIDTH);
  return text.length <= max ? text : `${text.slice(0, Math.max(1, max - 1))}…`;
}

function legendLayout(
  labels: readonly string[],
  colors: readonly string[]
): { entries: ChartLegendEntry[]; height: number } {
  const entries: ChartLegendEntry[] = [];
  let x = SIDE_MARGIN;
  let y = SIDE_MARGIN;
  for (const [i, label] of labels.entries()) {
    const width = LEGEND_SWATCH + 6 + label.length * CHAR_WIDTH + 16;
    if (x > SIDE_MARGIN && x + width > CHART_WIDTH - SIDE_MARGIN) {
      x = SIDE_MARGIN;
      y += LEGEND_ROW;
    }
    entries.push({ x, y, fill: colors[i], label });
    x += width;
  }
  return { entries, height: y + LEGEND_ROW };
}

export function likertChartLayout(
  items: readonly string[],
  bars: readonly (readonly DivergingSegment[])[],
  labels: readonly string[],
  colors: readonly string[]
): LikertChartLayout {
  const legend = legendLayout(labels, colors);
  const plotLeft = LABEL_COLUMN;
  const plotRight = CHART_WIDTH - RIGHT_MARGIN;
  const centre = (plotLeft + plotRight) / 2;
  const extent = divergingExtent(bars);
  const scale = (plotRight - plotLeft) / 2 / extent;
  const plotTop = legend.height + 10;
  const rows = items.map((name, i) => ({
    label: shorten(name, LABEL_COLUMN - 2 * SIDE_MARGIN),
    name,
    y: plotTop + i * ROW_PITCH + (ROW_PITCH - BAR_HEIGHT) / 2,
    segments: (bars[i] ?? []).map((segment, option) => {
      const width = segment.share * scale;
      const fill = colors[option];
      return {
        x: centre + segment.start * scale,
        width,
        fill,
        ink: inkOn(fill),
        share: segment.share,
        count: segment.count,
        code: segment.code,
        labelled: width >= MIN_LABELLED_SEGMENT,
      };
    }),
  }));
  const plotBottom = plotTop + items.length * ROW_PITCH;
  const steps = Math.round(extent / 0.25);
  const ticks = Array.from({ length: 2 * steps + 1 }, (_, i) => {
    const value = (i - steps) * 0.25;
    return { x: centre + value * scale, value };
  });
  return {
    width: CHART_WIDTH,
    height: plotBottom + AXIS_HEIGHT,
    labelX: LABEL_COLUMN - SIDE_MARGIN,
    barHeight: BAR_HEIGHT,
    plotTop,
    plotBottom,
    centre,
    rows,
    legend: legend.entries,
    ticks,
  };
}
